import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Family, Parent, Student } from '@/types/models';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { mapToBillingStatus, mapToInviteStatus } from '@/utils/typeUtils';

export const useFamiliesData = () => {
  const { user, organizationId } = useAuth();
  const [families, setFamilies] = useState<Family[]>([]);
  const [allParents, setAllParents] = useState<Parent[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [parentsByFamily, setParentsByFamily] = useState<Record<string, Parent[]>>({});
  const [studentsByFamily, setStudentsByFamily] = useState<Record<string, Student[]>>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const loadData = async () => {
      if (!organizationId) {
        console.log("No organization ID available, skipping data fetch");
        return;
      }

      try {
        setLoading(true);
        setConnectionError(false);
        
        // Use separate console logs to track each fetch operation
        console.log("Fetching families data for organization:", organizationId);
        
        const [familiesResult, parentsResult, studentsResult] = await Promise.all([
          supabase.from('families').select('*').eq('organization_id', organizationId),
          supabase.from('parents').select('*').eq('organization_id', organizationId),
          supabase.from('students').select('*').eq('organization_id', organizationId)
        ]);
        
        if (familiesResult.error) throw familiesResult.error;
        if (parentsResult.error) throw parentsResult.error;
        if (studentsResult.error) throw studentsResult.error;
        
        console.log(`Raw families data (count: ${familiesResult.data.length}):`, familiesResult.data.map(f => f.id));
        
        // Ensure we don't have duplicate families by using a Map with family ID as the key
        const familiesMap = new Map<string, Family>();
        
        familiesResult.data.forEach(family => {
          // Log if a duplicate is found
          if (familiesMap.has(family.id)) {
            console.warn(`Duplicate family ID found in database results: ${family.id}, name: ${family.name}`);
          }
          
          familiesMap.set(family.id, {
            ...family,
            billing_status: mapToBillingStatus(family.billing_status)
          });
        });
        
        // Convert back to array
        const mappedFamilies = Array.from(familiesMap.values()) as Family[];
        
        console.log(`Processed families (count: ${mappedFamilies.length}):`, mappedFamilies.map(f => f.id));
        
        setFamilies(mappedFamilies);
        
        const parentsWithEmail = parentsResult.data.map((parent) => {
          return {
            ...parent,
            invite_status: mapToInviteStatus(parent.invite_status),
            is_primary: parent.is_primary || false
          };
        });
        
        setAllParents(parentsWithEmail as Parent[]);
        setAllStudents(studentsResult.data as Student[]);
        
      } catch (error) {
        console.error("Failed to load data:", error);
        setConnectionError(true);
        toast.error("Database connection failed", {
          description: "Unable to fetch family data. Please try again later."
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [organizationId, refreshTrigger]);

  useEffect(() => {
    const parentsMap: Record<string, Parent[]> = {};
    allParents.forEach(parent => {
      if (!parentsMap[parent.family_id]) {
        parentsMap[parent.family_id] = [];
      }
      parentsMap[parent.family_id].push(parent);
    });
    setParentsByFamily(parentsMap);
  }, [allParents]);

  useEffect(() => {
    const studentsMap: Record<string, Student[]> = {};
    allStudents.forEach(student => {
      if (!studentsMap[student.family_id]) {
        studentsMap[student.family_id] = [];
      }
      studentsMap[student.family_id].push(student);
    });
    setStudentsByFamily(studentsMap);
  }, [allStudents]);

  return {
    families,
    allParents,
    allStudents,
    loading,
    connectionError,
    parentsByFamily,
    studentsByFamily,
    setFamilies,
    setAllParents,
    setAllStudents,
    refreshData
  };
};
